import { useEffect, useState } from "react";
import { toast } from "sonner";
import { marketingApi } from "@/lib/marketing";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Field, TextInput, Select, PURPOSES, STATUSES, copyToClipboard } from "./shared";
import LinkDetailPanel from "./LinkDetailPanel";

const emptyFilters = { search: "", purpose: "", channel: "", placement: "", campaign_key: "", offer_key: "", status: "", created_after: "", created_before: "" };

export default function LinksLibrary({ refreshToken, onChanged }) {
  const [links, setLinks] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [selected, setSelected] = useState(null);
  const [selectedTab, setSelectedTab] = useState("details");

  const openDetail = (link, tab) => {
    setSelected(link);
    setSelectedTab(tab);
  };

  const load = async () => {
    try {
      setLinks(await marketingApi.listLinks(filters));
    } catch (e) {
      toast.error(e.message || "Could not load links");
    }
  };

  useEffect(() => { load(); }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const toggleStatus = async (link) => {
    try {
      if (link.metadata.status === "active") await marketingApi.deactivateLink(link.link_id);
      else await marketingApi.reactivateLink(link.link_id);
      toast.success("Updated");
      load();
      onChanged();
    } catch (e) {
      toast.error(e.message || "Failed");
    }
  };

  const duplicate = async (link) => {
    try {
      const dup = await marketingApi.duplicateLink(link.link_id);
      toast.success(`Duplicated as ${dup.link_id}`);
      load();
      onChanged();
    } catch (e) {
      toast.error(e.message || "Duplicate failed");
    }
  };

  return (
    <div className="bg-paper border border-ink/12 overflow-hidden" data-testid="links-library">
      <div className="p-4 border-b border-ink/10 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-serif text-xl text-ink">Links library ({links.length})</h2>
          <button type="button" onClick={() => setFilters(emptyFilters)} data-testid="clear-filters-button" className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta">Clear filters</button>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <TextInput placeholder="Search name…" value={filters.search} onChange={(e) => setFilter("search")(e.target.value)} data-testid="filter-search-input" />
          <Select value={filters.purpose} onChange={setFilter("purpose")} options={PURPOSES} includeEmpty data-testid="filter-purpose-select" />
          <Select value={filters.status} onChange={setFilter("status")} options={STATUSES} includeEmpty data-testid="filter-status-select" />
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <TextInput placeholder="Channel" value={filters.channel} onChange={(e) => setFilter("channel")(e.target.value)} data-testid="filter-channel-input" />
          <TextInput placeholder="Placement" value={filters.placement} onChange={(e) => setFilter("placement")(e.target.value)} data-testid="filter-placement-input" />
          <TextInput placeholder="Campaign" value={filters.campaign_key} onChange={(e) => setFilter("campaign_key")(e.target.value)} data-testid="filter-campaign-input" />
          <TextInput placeholder="Offer" value={filters.offer_key} onChange={(e) => setFilter("offer_key")(e.target.value)} data-testid="filter-offer-input" />
        </div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <Field label="Created after"><TextInput type="date" value={filters.created_after} onChange={(e) => setFilter("created_after")(e.target.value)} data-testid="filter-date-after-input" /></Field>
          <Field label="Created before"><TextInput type="date" value={filters.created_before} onChange={(e) => setFilter("created_before")(e.target.value)} data-testid="filter-date-before-input" /></Field>
          <button type="button" onClick={load} data-testid="apply-filters-button" className="bg-ink text-paper py-2.5 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors">Apply filters</button>
        </div>
      </div>

      <Table data-testid="links-table">
        <TableHeader>
          <TableRow>
            <TableHead className="font-body uppercase tracking-wide text-xs">Name</TableHead>
            <TableHead className="font-body uppercase tracking-wide text-xs">Short URL</TableHead>
            <TableHead className="font-body uppercase tracking-wide text-xs">Purpose</TableHead>
            <TableHead className="font-body uppercase tracking-wide text-xs">Channel</TableHead>
            <TableHead className="font-body uppercase tracking-wide text-xs">Status</TableHead>
            <TableHead className="font-body uppercase tracking-wide text-xs">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {links.length === 0 && (
            <TableRow><TableCell colSpan={6} className="font-body text-ink-soft text-center py-8" data-testid="links-table-empty">No tracking links match these filters.</TableCell></TableRow>
          )}
          {links.map((l) => (
            <TableRow key={l.link_id} data-testid={`link-row-${l.link_id}`}>
              <TableCell className="font-body text-ink">{l.metadata.name}</TableCell>
              <TableCell className="font-body text-ink-soft text-xs">{l.short_url}</TableCell>
              <TableCell className="font-body text-ink-soft text-xs">{l.metadata.purpose}</TableCell>
              <TableCell className="font-body text-ink-soft text-xs">{l.metadata.channel || "—"}</TableCell>
              <TableCell>
                <Badge className={l.metadata.status === "active" ? "bg-mustard text-ink border-transparent" : ""} data-testid={`status-badge-${l.link_id}`}>{l.metadata.status}</Badge>
              </TableCell>
              <TableCell className="flex flex-wrap gap-2">
                <button type="button" onClick={() => copyToClipboard(l.short_url, "Short URL")} data-testid={`copy-link-${l.link_id}`} className="font-body text-xs text-terracotta hover:underline">Copy</button>
                <a href={l.destination_url} target="_blank" rel="noreferrer" data-testid={`open-link-${l.link_id}`} className="font-body text-xs text-terracotta hover:underline">Open</a>
                <button type="button" onClick={() => openDetail(l, "details")} data-testid={`view-details-${l.link_id}`} className="font-body text-xs text-terracotta hover:underline">Details</button>
                <button type="button" onClick={() => openDetail(l, "qr")} data-testid={`qr-link-${l.link_id}`} className="font-body text-xs text-terracotta hover:underline">QR</button>
                <button type="button" onClick={() => duplicate(l)} data-testid={`duplicate-link-${l.link_id}`} className="font-body text-xs text-terracotta hover:underline">Duplicate</button>
                <button type="button" onClick={() => toggleStatus(l)} data-testid={`toggle-status-${l.link_id}`} className="font-body text-xs text-terracotta hover:underline">
                  {l.metadata.status === "active" ? "Deactivate" : "Reactivate"}
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selected && (
        <LinkDetailPanel
          link={selected}
          open={!!selected}
          defaultTab={selectedTab}
          onOpenChange={(o) => { if (!o) { setSelected(null); setSelectedTab("details"); } }}
          onChanged={async () => { await load(); onChanged(); const fresh = await marketingApi.getLink(selected.link_id); setSelected(fresh); }}
        />
      )}
    </div>
  );
}
